<template>
  <div class="container-fluid px-5 mt-4">

    <!-- Nadpis stránky -->
    <div class="row mb-4">
      <div class="col-12">
        <h1 class="credits-page__title">{{ $t('credits.credits') }}</h1>
      </div>
    </div>

    <!-- Sekce Můj zůstatek -->
    <div class="dashboard-section-header mb-3 mt-5">
      <div class="d-flex align-items-center gap-2">
        <img :src="iconFlash" alt="" class="dashboard-section-header__icon credits-section-icon">
        <span class="dashboard-section-header__title">{{ $t('credits.myBalance') }}</span>
      </div>
    </div>

    <!-- Karta zůstatku -->
    <div class="row">
      <div class="col-12">
        <div class="credits-balance">

          <!-- Kruh — zatím bez funkce -->
          <div v-if=false class="credits-balance__circle"></div>

          <!-- Počet kreditů -->
          <div class="credits-balance__info">
            <div class="credits-balance__count">
              <span class="credits-balance__current" :class="{ 'credits-balance__current--empty': !isLoading && client.credits === 0 }">{{ isLoading ? '—' : client.credits }}</span>
              <span v-if=false class="credits-balance__separator">&thinsp;/&thinsp;</span>
              <span v-if=false class="credits-balance__total">{{ isLoading ? '—' : creditsTotal }}</span>
            </div>
            <p class="credits-balance__label">{{ $t('credits.creditsRemaining') }}</p>
          </div>

          <!-- Tlačítko Get More Credits -->
          <div class="ms-auto">
            <button @click="showPurchaseModal = true" class="btn btn-primary credits-balance__btn">
              {{ $t('credits.getMoreCredits') }}
              <img :src="iconFlash" alt="" class="credits-balance__btn-icon">
            </button>
          </div>

        </div>
      </div>
    </div>

    <!-- Sekce Poslední nákupy -->
    <div class="dashboard-section-header mb-3 mt-5">
      <div class="d-flex align-items-center gap-2">
        <img :src="iconPast" alt="" class="dashboard-section-header__icon" style="width:17px;height:17px;">
        <span class="dashboard-section-header__title">{{ $t('credits.pastPurchases') }}</span>
        <span class="count-badge ms-2">{{ purchases.length }}</span>
      </div>
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <div v-if="purchasesLoading" class="cert-list">
          <div v-for="n in 3" :key="n" class="cert-list__row">
            <div class="cert-list__icon-wrap"></div>
            <div class="cert-list__col"><div class="cert-list__value">&nbsp;</div></div>
          </div>
        </div>

        <div v-else-if="purchases.length === 0" class="d-flex justify-content-center mt-4 mb-5">
          <EmptyStateComponent
            :icon="iconPast"
            :icon-style="{ width: '17px', height: '17px', filter: 'brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)' }"
            :title="$t('credits.noPastPurchases')"
          />
        </div>

        <div v-else class="cert-list cert-list--stacked-mobile">
          <div v-for="purchase in purchases" :key="purchase.id" class="cert-list__row">

            <!-- Ikona -->
            <div class="cert-list__icon-wrap">
              <img :src="iconFlash" alt="" class="cert-list__file-icon credits-purchase__flash-icon">
            </div>

            <!-- Kredity + cena -->
            <div class="cert-list__col">
              <span class="cert-list__label">{{ $t('credits.credits') }}</span>
              <span class="cert-list__value">
                {{ purchase.amount }}
                <span v-if="purchase.amount_paid">({{ formatPrice(purchase.amount_paid, purchase.currency) }})</span>
              </span>
            </div>

            <!-- Datum nákupu -->
            <div class="cert-list__col cert-list__col--date">
              <span class="cert-list__label">{{ $t('credits.purchased') }}</span>
              <span class="cert-list__value">{{ formatDateShort(purchase.created_at) }}, {{ formatTime(purchase.created_at) }}</span>
            </div>

            <!-- Tlačítko View Invoice -->
            <div v-if=false class="cert-list__action">
              <a href="#" class="btn cert-list__view-btn">
                {{ $t('credits.viewInvoice') }}
              </a>
            </div>

          </div>
        </div>
      </div>
    </div>

  <!-- Modal nákupu kreditů -->
  <CreditPurchaseModal v-if="showPurchaseModal" @close="showPurchaseModal = false" />

  </div>
</template>

<script>
import http from '@/http';
import iconFlash from '/public/img/new-design/icons/flash.svg';
import iconPast from '/public/img/new-design/icons/past.svg';
import CreditPurchaseModal from '../components/credits/CreditPurchaseModal.vue';
import { loadingState } from '@/loading.js';

const EMPTY_CLIENT = {
  name: '',
  email: '',
  address: { line_1: '', line_2: '', city: '', country_code: '', postal_code: '' },
  verification: { document_type: '', document_number: '', expiration_date: '' },
};

export default {
  components: { CreditPurchaseModal },

  data() {
    return {
      client: { ...EMPTY_CLIENT, credits: 0 },
      creditsTotal: 0,
      isLoading: true,
      purchases: [],
      purchasesLoading: true,
      showPurchaseModal: false,
      iconFlash,
      iconPast,
    };
  },

  mounted() {
    this.getAuthClient();
    this.getCreditPurchases();
  },

  methods: {
    getAuthClient() {
      http.get('/get-auth-client').then((response) => {
        this.client = response.data.client;
        if (!this.client.address) this.client.address = { ...EMPTY_CLIENT.address };
        if (!this.client.verification) this.client.verification = { ...EMPTY_CLIENT.verification };
        this.creditsTotal = response.data.client.credits_total ?? this.client.credits;
        this.isLoading = false;
        loadingState.active = false;
      });
    },

    getCreditPurchases() {
      http.get('/get-credit-purchases').then((response) => {
        this.purchases = response.data.purchases;
        this.purchasesLoading = false;
      });
    },

    formatPrice(amountPaid, currency) {
      if (!amountPaid) return '';
      const locale = this.$i18n.locale === 'cs' ? 'cs-CZ' : 'en-US';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency?.toUpperCase() || 'CZK',
        maximumFractionDigits: 2,
      }).format(amountPaid / 100);
    },

    formatDateShort(dateStr) {
      const d = new Date(dateStr);
      const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
      return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
    },

    formatTime(dateStr) {
      const d = new Date(dateStr);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
  },
};
</script>